import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Users, Plus, Lock, Crown, ArrowLeft, Search, UserPlus, LogOut } from "lucide-react";
import Navbar from "@/components/Navbar";

// Badge Component
const UserBadge = ({ badge }) => {
  if (!badge) return null;
  const badgeStyles = {
    pro: "bg-gradient-to-r from-amber-500 to-orange-500 text-white",
    access: "bg-gradient-to-r from-blue-500 to-cyan-500 text-white",
    admin: "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase font-bold ${badgeStyles[badge] || ''}`}>
      {badge}
    </span>
  );
};

const GroupsPage = () => {
  const { user, token, canDebate } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Create form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const isPro = user?.subscription_tier === "pro" || user?.role === "admin";
  const hasAccess = user?.subscription_tier === "access" || isPro;

  useEffect(() => {
    fetchGroups();
    if (user) {
      fetchMyGroups();
    }
  }, [user]);

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API}/groups`);
      setGroups(res.data);
    } catch (e) {
      console.error("Failed to fetch groups");
    } finally {
      setLoading(false);
    }
  };

  const fetchMyGroups = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/users/${user.user_id}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyGroups(res.data.groups || []);
    } catch (e) {
      console.error("Failed to fetch my groups");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    setCreating(true);
    try {
      await axios.post(`${API}/groups`, { name, description }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Group created!");
      setName("");
      setDescription("");
      setShowCreate(false);
      fetchGroups();
      fetchMyGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create group");
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async (groupId) => {
    if (!hasAccess) {
      toast.error("Spardom Access required to join groups");
      navigate("/subscription");
      return;
    }
    
    try {
      await axios.post(`${API}/groups/${groupId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Joined group!");
      fetchGroups();
      fetchMyGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to join group");
    }
  };

  const handleLeave = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Left group");
      fetchGroups();
      fetchMyGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to leave group");
    }
  };

  const isMyGroup = (groupId) => myGroups.some(g => g.group_id === groupId);

  const filteredGroups = searchQuery 
    ? groups.filter(g => g.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : groups;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              Debate Groups
            </h1>
            <p className="text-muted-foreground mt-1">
              Join communities of debaters with shared interests
            </p>
          </div>
          
          {isPro && (
            <Button onClick={() => setShowCreate(!showCreate)} className="btn-electric">
              <Plus className="w-4 h-4 mr-2" />
              Create Group
            </Button>
          )}
        </div>

        {/* Access Required Banner */}
        {user && !hasAccess && (
          <div className="mb-6 bg-secondary/10 border border-secondary/30 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-secondary" />
              <div>
                <p className="font-medium">Spardom Access Required</p>
                <p className="text-sm text-muted-foreground">Upgrade to join and view group members</p>
              </div>
            </div>
            <Link to="/subscription">
              <Button size="sm" variant="secondary">Upgrade - $1.99/mo</Button>
            </Link>
          </div>
        )}

        {/* Create Group Form */}
        {showCreate && isPro && (
          <div className="mb-8 bg-card/30 border border-white/10 rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4">Create New Group</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Group Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Winners Circle"
                  className="bg-zinc-900/50 border-white/10"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What's this group about?"
                  className="bg-zinc-900/50 border-white/10"
                  rows={3}
                />
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={creating || !name.trim()}>
                  {creating ? "Creating..." : "Create Group"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search groups..."
              className="pl-10 bg-zinc-900/50 border-white/10"
            />
          </div>
        </div>

        {/* My Groups */}
        {myGroups.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-4">My Groups</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myGroups.map(group => (
                <Link 
                  key={group.group_id} 
                  to={`/groups/${group.group_id}`}
                  className="bg-card/50 border border-primary/30 rounded-lg p-4 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold">{group.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {group.description || "No description"}
                      </p>
                    </div>
                    <Badge variant="secondary" className="shrink-0">Joined</Badge>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="w-4 h-4" />
                    {group.member_count} members
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* All Groups */}
        <div>
          <h2 className="text-lg font-bold mb-4">
            {myGroups.length > 0 ? "Discover Groups" : "All Groups"}
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="text-center py-12 bg-card/30 rounded-lg border border-white/10">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-bold mb-2">No groups found</h3>
              <p className="text-muted-foreground text-sm">
                {searchQuery ? "Try a different search" : "Be the first to create a group!"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredGroups.filter(g => !isMyGroup(g.group_id)).map(group => (
                <div 
                  key={group.group_id}
                  className="bg-card/30 border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold truncate">{group.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {group.description || "No description"}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="w-4 h-4" />
                      {group.member_count} members
                    </div>
                    
                    {user ? (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleJoin(group.group_id)}
                        disabled={!hasAccess}
                      >
                        {hasAccess ? (
                          <>
                            <UserPlus className="w-3 h-3 mr-1" />
                            Join
                          </>
                        ) : (
                          <>
                            <Lock className="w-3 h-3 mr-1" />
                            Locked
                          </>
                        )}
                      </Button>
                    ) : (
                      <Link to="/login">
                        <Button size="sm" variant="outline">
                          Login to Join
                        </Button>
                      </Link>
                    )}
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2 text-xs text-muted-foreground">
                    <Crown className="w-3 h-3" />
                    Created by {group.owner_username}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pro CTA */}
        {user && !isPro && (
          <div className="mt-12 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg p-6 text-center">
            <Crown className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h3 className="text-xl font-bold mb-2">Want to Create Groups?</h3>
            <p className="text-muted-foreground mb-4">
              Pro Debaters can create and manage their own debate groups
            </p>
            <Link to="/subscription">
              <Button className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                Upgrade to Pro - $4.99/mo
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupsPage;
